import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { Layout, ScrollToTop } from '@/components/layout';
import DealerLayout from '@/components/dealer/DealerLayout';
import DealerRequireAuth from '@/components/dealer/DealerRequireAuth';
import { ToastContainer } from '@/components/ui';
import { useFavoriteStore, useUserAuthStore } from '@/store';

const HomePage = lazy(() => import('@/pages/HomePage').then((module) => ({ default: module.HomePage })));
const SearchPage = lazy(() => import('@/pages/SearchPage').then((module) => ({ default: module.SearchPage })));
const CarDetailPage = lazy(() => import('@/pages/CarDetailPage').then((module) => ({ default: module.CarDetailPage })));
const FavoritesPage = lazy(() => import('@/pages/FavoritesPage').then((module) => ({ default: module.FavoritesPage })));
const SellCarPage = lazy(() => import('@/pages/SellCarPage').then((module) => ({ default: module.SellCarPage })));
const ComparePage = lazy(() => import('@/pages/ComparePage').then((module) => ({ default: module.ComparePage })));
const BrandsPage = lazy(() => import('@/pages/BrandsPage').then((module) => ({ default: module.BrandsPage })));
const AdminPage = lazy(() => import('@/pages/AdminPage').then((module) => ({ default: module.AdminPage })));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then((module) => ({ default: module.NotFoundPage })));
const SupportPage = lazy(() => import('@/pages/SupportPage').then((module) => ({ default: module.SupportPage })));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage').then((module) => ({ default: module.PrivacyPage })));
const LoginPage = lazy(() => import('@/pages/LoginPage').then((module) => ({ default: module.LoginPage })));
const RegisterPage = lazy(() => import('@/pages/RegisterPage').then((module) => ({ default: module.RegisterPage })));
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage').then((module) => ({ default: module.VerifyEmailPage })));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage').then((module) => ({ default: module.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage').then((module) => ({ default: module.ResetPasswordPage })));
const ResendVerificationPage = lazy(() => import('@/pages/ResendVerificationPage').then((module) => ({ default: module.ResendVerificationPage })));

const AdminVerificationQueuePage = lazy(() => import('@/pages/admin/AdminVerificationQueuePage'));
const DealerLoginPage = lazy(() => import('@/pages/dealer/DealerLoginPage'));
const DealerRegisterPage = lazy(() => import('@/pages/dealer/DealerRegisterPage'));
const DealerVerifyEmailPage = lazy(() => import('@/pages/dealer/DealerVerifyEmailPage'));
const DealerForgotPasswordPage = lazy(() => import('@/pages/dealer/DealerForgotPasswordPage'));
const DealerResetPasswordPage = lazy(() => import('@/pages/dealer/DealerResetPasswordPage'));
const DealerDashboardPage = lazy(() => import('@/pages/dealer/DealerDashboardPage'));
const DealerCompanyProfilePage = lazy(() => import('@/pages/dealer/DealerCompanyProfilePage'));
const DealerTeamPage = lazy(() => import('@/pages/dealer/DealerTeamPage'));
const DealerAcceptInvitePage = lazy(() => import('@/pages/dealer/DealerAcceptInvitePage'));
const DealerCarListPage = lazy(() => import('@/pages/dealer/DealerCarListPage'));
const DealerCarWizardPage = lazy(() => import('@/pages/dealer/DealerCarWizardPage'));
const DealerBulkImportPage = lazy(() => import('@/pages/dealer/DealerBulkImportPage'));
const DealerIndexPage = lazy(() => import('@/pages/public/DealerIndexPage'));
const DealerShowroomPage = lazy(() => import('@/pages/public/DealerShowroomPage'));

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="h-10 w-10 rounded-full border-4 border-gray-200 border-t-primary-600 animate-spin" />
    </div>
  );
}

function LayoutWrapper() {
  return (
    <Layout>
      <Outlet />
    </Layout>
  );
}

function DealerProtectedLayout({ children, requireOwner = false }: { children: ReactNode; requireOwner?: boolean }) {
  return (
    <DealerRequireAuth requireOwner={requireOwner}>
      <DealerLayout>{children}</DealerLayout>
    </DealerRequireAuth>
  );
}

function App() {
  const fetchFavoriteIds = useFavoriteStore((state) => state.fetchFavoriteIds);
  const checkUserAuth = useUserAuthStore((state) => state.checkAuth);

  useEffect(() => {
    fetchFavoriteIds();
    checkUserAuth();
  }, [fetchFavoriteIds, checkUserAuth]);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <ToastContainer />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/login" element={<AdminPage />} />
          <Route path="/admin/verification-queue" element={<AdminVerificationQueuePage />} />

          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/resend-verification" element={<ResendVerificationPage />} />

          <Route path="/dealer" element={<Navigate to="/dealer/dashboard" replace />} />
          <Route path="/dealer/login" element={<DealerLoginPage />} />
          <Route path="/dealer/register" element={<DealerRegisterPage />} />
          <Route path="/dealer/verify-email" element={<DealerVerifyEmailPage />} />
          <Route path="/dealer/forgot-password" element={<DealerForgotPasswordPage />} />
          <Route path="/dealer/reset-password" element={<DealerResetPasswordPage />} />
          <Route path="/dealer/accept-invite" element={<DealerAcceptInvitePage />} />
          <Route path="/dealer/dashboard" element={<DealerProtectedLayout><DealerDashboardPage /></DealerProtectedLayout>} />
          <Route path="/dealer/company" element={<DealerProtectedLayout requireOwner><DealerCompanyProfilePage /></DealerProtectedLayout>} />
          <Route path="/dealer/team" element={<DealerProtectedLayout requireOwner><DealerTeamPage /></DealerProtectedLayout>} />
          <Route path="/dealer/cars" element={<DealerProtectedLayout><DealerCarListPage /></DealerProtectedLayout>} />
          <Route path="/dealer/cars/new" element={<DealerRequireAuth><DealerCarWizardPage /></DealerRequireAuth>} />
          <Route path="/dealer/cars/:id/edit" element={<DealerRequireAuth><DealerCarWizardPage /></DealerRequireAuth>} />
          <Route path="/dealer/bulk-import" element={<DealerProtectedLayout><DealerBulkImportPage /></DealerProtectedLayout>} />

          <Route element={<LayoutWrapper />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/cars/:id" element={<CarDetailPage />} />
            <Route path="/favorites" element={<FavoritesPage />} />
            <Route path="/sell" element={<SellCarPage />} />
            <Route path="/compare" element={<ComparePage />} />
            <Route path="/brands" element={<BrandsPage />} />
            <Route path="/dealers" element={<DealerIndexPage />} />
            <Route path="/dealers/:slug" element={<DealerShowroomPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
